import React, { useEffect, useMemo } from "react";
import {
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Label,
} from "recharts";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import { getPerformanceTrendSagaAction } from "../../../../../redux/saga/partner-program/saga-actions";
import { TrendChartKey, PartnerTierListType } from "../../../../../redux/slices/partner-program/Types";
import {
  TIME_PERIODS,
  formatDateLabel,
  formatNumber,
  calculateTrendChange,
  getLatestTrendInfo,
  getTierNamesFromTierList,
  getTierNameToIndexMap,
} from "./helpers";

export type YAxisConfigType = 
  | { type: "tierNames"; tierList: PartnerTierListType }
  | { type: "maxPoints"; tierList: PartnerTierListType }
  | { type: "dynamic" };

// Trend Icon Component
type TrendIconProps = {
  isUp: boolean;
  size?: number;
};

const TrendIcon: React.FC<TrendIconProps> = ({ isUp, size = 24 }) => {
  const color = isUp ? "#10B981" : "#EF4444";
  
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        transform: isUp ? "none" : "scaleY(-1)"
      }}
    >
      <path
        d="M16 7H22V13"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22 7L13.5 15.5L8.5 10.5L2 17"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

type TrendChartProps = {
  chartKey: TrendChartKey;
  title: string;
  partnerId: number;
  yAxisConfig: YAxisConfigType;
  yAxisLabel?: string;
};

const TrendChart: React.FC<TrendChartProps> = ({
  chartKey,
  title,
  partnerId,
  yAxisConfig,
  yAxisLabel = "",
}) => {
  const dispatch = useAppDispatch();
  const trendData = useAppSelector(
    (state) => state.partner_program.performanceTrends[chartKey]
  );

  const { labels, values, loading, error, selectedPeriod } = trendData;

  // Fetch data on mount and when period changes
  useEffect(() => {
    if (partnerId) {
      dispatch(
        getPerformanceTrendSagaAction({
          chartKey,
          partnerId,
          range: selectedPeriod,
        })
      );
    }
  }, [dispatch, chartKey, partnerId, selectedPeriod]);

  // Handle period change
  const handlePeriodChange = (period: string) => {
    dispatch(
      getPerformanceTrendSagaAction({
        chartKey,
        partnerId,
        range: period,
      })
    );
  };

  // Transform data for AreaMapChart
  // AreaMapChart expects: { key: string, [dataKey]: number }[]
  const chartData = useMemo(() => {
    if (yAxisConfig.type === "tierNames") {
      // For tier status chart, convert tier names to numeric indices
      const tierMap = getTierNameToIndexMap(yAxisConfig.tierList);
      return labels.map((label, index) => {
        const tierName = values[index] as string;
        const tierIndex = tierMap[tierName];
        return {
          key: formatDateLabel(label),
          value: tierIndex !== undefined ? tierIndex : 0,
          tierName: tierName || "N/A",
        };
      });
    }
    
    return labels.map((label, index) => ({
      key: formatDateLabel(label),
      value: values[index] as number,
    }));
  }, [labels, values, yAxisConfig]);

  // Get latest info and trend change
  const { latestValue } = getLatestTrendInfo(labels, values);
  const trendChange = useMemo(() => {
    const numericValues = values.filter((v): v is number => typeof v === "number");
    return calculateTrendChange(numericValues);
  }, [values]);

  // Get today's date formatted
  const todayDate = useMemo(() => {
    const today = new Date();
    const day = today.getDate();
    const month = today.toLocaleString("en-US", {
      month: "short",
    });
    const year = today.getFullYear();
    return `${day} ${month} ${year}`;
  }, []);

  // Display value based on chart type
  const displayValue = useMemo(() => {
    if (yAxisConfig.type === "tierNames") {
      const tierValue = latestValue as string;
      // Show N/A if tier is not available (null, undefined, empty, or not in tier list)
      if (!tierValue || tierValue === "") {
        return "N/A";
      }
      const tierNames = getTierNamesFromTierList(yAxisConfig.tierList);
      if (!tierNames.includes(tierValue)) {
        return "N/A";
      }
      return tierValue;
    }
    return latestValue !== null ? formatNumber(latestValue as number) : "-";
  }, [yAxisConfig, latestValue]);

  // Calculate if trend is up or down (current >= previous means up)
  const isTrendUp = useMemo(() => {
    if (values.length < 2) return true; // Default to up if not enough data
    
    if (yAxisConfig.type === "tierNames") {
      // For tier chart, compare tier indices
      const tierMap = getTierNameToIndexMap(yAxisConfig.tierList);
      const currentTier = values[values.length - 1] as string;
      const previousTier = values[values.length - 2] as string;
      const currentIndex = tierMap[currentTier] ?? 0;
      const previousIndex = tierMap[previousTier] ?? 0;
      return currentIndex >= previousIndex;
    }
    
    // For numeric values
    const currentValue = values[values.length - 1] as number;
    const previousValue = values[values.length - 2] as number;
    return currentValue >= previousValue;
  }, [values, yAxisConfig]);

  return (
    <div className="chart-card-wrapper">
      <div className="chart-card-header">
        <h3 className="chart-card-title">{title}</h3>
        <div className="chart-card-meta">
          <Iconography icon="calendar" size={IconographySizes.Small} />
          <span>{todayDate}</span>
        </div>
      </div>

      <div className="chart-card-stats">
        <div className="stat-value-wrapper">
          <TrendIcon isUp={isTrendUp} size={28} />
          <span className="stat-value">{displayValue}</span>
        </div>
        {yAxisConfig.type !== "tierNames" && trendChange.change !== 0 && (
          <div className="stat-change-wrapper">
            <span
              className="stat-change-value"
              style={{ color: trendChange.isPositive ? "#10b981" : "#ef4444" }}
            >
              {trendChange.isPositive ? "+" : "-"}
              {formatNumber(Math.abs(trendChange.change))}
              {trendChange.canCalculatePercentage && ` (${trendChange.percentage.toFixed(2)}%)`}
            </span>
            <Iconography
              icon={trendChange.isPositive ? "arrowUp" : "arrowDown"}
              size={IconographySizes.Small}
              className="stat-arrow"
              style={{ color: trendChange.isPositive ? "#10b981" : "#ef4444" }}
            />
          </div>
        )}
      </div>

      <div className="chart-card-content">
        {loading ? (
          <div className="chart-loading">
            <Loader variant={LoaderSizes.small} />
          </div>
        ) : error ? (
          <div className="chart-error">
            <span>{error}</span>
          </div>
        ) : chartData.length === 0 ? (
          <div className="chart-empty">
            <span>No data available</span>
          </div>
        ) : (
          <div className="area-chart">
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 20, bottom: 30 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="#D8D8D8" />
                <XAxis 
                  dataKey="key" 
                  fontSize={10} 
                  axisLine={false} 
                  tickLine={false}
                >
                  <Label value="Month" position="bottom" offset={10} fontSize={12} fill="#6b7280" />
                </XAxis>
                {yAxisConfig.type === "tierNames" ? (
                  <YAxis
                    type="number"
                    domain={[0, getTierNamesFromTierList(yAxisConfig.tierList).length - 1]}
                    ticks={getTierNamesFromTierList(yAxisConfig.tierList).map((_, i) => i)}
                    tickFormatter={(value: number) => {
                      const tierNames = getTierNamesFromTierList(yAxisConfig.tierList);
                      return tierNames[value] || "";
                    }}
                    fontSize={10}
                    axisLine={false}
                    tickLine={false}
                    width={75}
                  >
                    <Label value={yAxisLabel || "Tier"} position="insideLeft" angle={-90} offset={0} fontSize={12} fill="#6b7280" style={{ textAnchor: 'middle' }} />
                  </YAxis>
                ) : (
                  <YAxis
                    fontSize={10}
                    axisLine={false}
                    tickLine={false}
                    width={60}
                    tickFormatter={(value: number) => formatNumber(value)}
                  >
                    <Label value={yAxisLabel} position="insideLeft" angle={-90} offset={0} fontSize={12} fill="#6b7280" style={{ textAnchor: 'middle' }} />
                  </YAxis>
                )}
                <Tooltip
                  formatter={(value: number) => {
                    if (yAxisConfig.type === "tierNames") {
                      const tierNames = getTierNamesFromTierList(yAxisConfig.tierList);
                      return [tierNames[value] || "N/A", "Tier"];
                    }
                    return [formatNumber(value), yAxisLabel || title];
                  }}
                />
                <Area
                  type="linear"
                  dataKey="value"
                  stroke="#62BED2"
                  fill="#62BED2"
                  fillOpacity={0.2}
                  strokeWidth={2}
                  dot={{ fill: "#62BED2", stroke: "#fff", strokeWidth: 2, r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="time-period-buttons">
        {TIME_PERIODS.map((period) => (
          <button
            key={period.value}
            className={`period-btn ${selectedPeriod === period.value ? "active" : ""}`}
            onClick={() => handlePeriodChange(period.value)}
            disabled={loading}
          >
            {period.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default TrendChart;
